// Přepisy: složky klientů (vlevo), ve složce shrnutí klienta (Samova pipeline), schůzky s přepisy a chat
// přímo ve složce (uživatel zůstává tady; konverzace patří ke složce a po návratu na ni naváže).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CaretDownIcon, CaretLeftIcon, ChatCircleIcon, FolderIcon, MagnifyingGlassIcon, PlusIcon, SparkleIcon } from '@phosphor-icons/react';
import { Badge, Button, EmptyState, IconButton, Input, Spinner, useToast } from '@smworks-cz/ui-kit';
import type { AttachmentDTO, ClientSummaryDTO, MeDTO, TranscriptClientDTO, TranscriptMeetingDTO, TranscriptStatus } from '@sam/shared';
import { api, ApiError } from '../lib/api';
import { navigate } from '../lib/router';
import { useConversation } from '../lib/queries';
import { Markdown } from '../components/Markdown';
import { followTurn, useAnyLiveTurn } from '../lib/turns';
import { sendMessage } from '../lib/send';
import { openTranscript, closeTranscript, useTranscriptPanel } from '../lib/transcript';
import { Composer } from '../components/Composer';
import { AssistantMessage, UserMessage } from '../components/Message';
import { TranscriptPanel } from '../components/TranscriptPanel';

const STATUS: Record<TranscriptStatus, { label: string; variant: 'success' | 'info' | 'warning' | 'danger' }> = {
  ready: { label: 'přepis hotový', variant: 'success' },
  processing: { label: 'zpracovává se', variant: 'info' },
  queued: { label: 've frontě', variant: 'warning' },
  error: { label: 'chyba zpracování', variant: 'danger' },
};

const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
const fmtStart = (start: string) => {
  const [d, t] = start.split('T');
  const [y, m, day] = d.split('-').map(Number);
  return `${day}. ${m}. ${y !== new Date().getFullYear() ? `${y} ` : ''}${t}`;
};

export function Transcripts({ slug, me }: { slug: string | null; me: MeDTO }) {
  const panel = useTranscriptPanel();
  useEffect(() => closeTranscript, [slug]);
  const [q, setQ] = useState('');
  const clients = useQuery({
    queryKey: ['transcripts', 'clients'],
    queryFn: async () => (await api<{ rows: TranscriptClientDTO[] }>('GET', '/transcripts/clients')).rows,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const shown = useMemo(() => (clients.data ?? []).filter((c) => !q || norm(c.name).includes(norm(q))), [clients.data, q]);
  const current = clients.data?.find((c) => c.slug === slug) ?? null;

  useEffect(() => {
    document.title = current ? `${current.name} · Přepisy · SAM` : 'Přepisy · SAM';
    return () => {
      document.title = 'SAM';
    };
  }, [current?.name]);

  return (
    <div className={`chat-layout${panel.ref ? ' with-panel' : ''}`}>
      <div className={`tr-page${slug ? ' has-client' : ''}`}>
        <aside className="tr-clients" aria-label="Složky klientů">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat klienta" icon={<MagnifyingGlassIcon size={14} />} clearable fullWidth aria-label="Hledat klienta" />
          {clients.isLoading && <Spinner />}
          {clients.error && <EmptyState size="sm" preset="error" title="Složky teď nejdou načíst" description={(clients.error as Error).message} />}
          <nav className="tr-list">
            {shown.map((c) => (
              <a
                key={c.slug}
                href={`/prepisy/${encodeURIComponent(c.slug)}`}
                className={`tr-client${c.slug === slug ? ' on' : ''}`}
                aria-current={c.slug === slug ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/prepisy/${encodeURIComponent(c.slug)}`);
                }}
              >
                <FolderIcon size={17} />
                <span className="tr-client-t">
                  <b>{c.name}</b>
                  <small>
                    {c.meetings} {c.meetings === 1 ? 'schůzka' : c.meetings >= 2 && c.meetings <= 4 ? 'schůzky' : 'schůzek'}
                    {c.lastMeetingAt ? ` · naposledy ${fmtStart(c.lastMeetingAt).split(' ').slice(0, 2).join(' ')}` : ''}
                  </small>
                </span>
                {!!c.pending && <span className="tr-pending" title={`${c.pending} nahrávek čeká na přepis`}>{c.pending}</span>}
              </a>
            ))}
            {clients.data && !shown.length && <p className="muted small">Nic nenalezeno.</p>}
          </nav>
        </aside>
        <section className="tr-main">
          {!slug && (
            <div className="page-center">
              <EmptyState preset="no-results" title="Vyberte složku klienta" description="Uvidíte přehled klienta, přepisy schůzek a můžete se rovnou zeptat Sama." />
            </div>
          )}
          {slug && <ClientFolder key={slug} slug={slug} client={current} me={me} />}
        </section>
      </div>
      <TranscriptPanel />
    </div>
  );
}

function ClientFolder({ slug, client, me }: { slug: string; client: TranscriptClientDTO | null; me: MeDTO }) {
  const name = client?.name ?? slug;
  const meetings = useQuery({
    queryKey: ['transcripts', 'meetings', slug],
    queryFn: async () => (await api<{ rows: TranscriptMeetingDTO[] }>('GET', `/transcripts/clients/${encodeURIComponent(slug)}`)).rows,
    refetchInterval: 60_000,
  });


  return (
    <div className="tr-folder">
      <div className="tr-head">
        <button type="button" className="tr-back" onClick={() => navigate('/prepisy')} aria-label="Zpět na složky">
          <CaretLeftIcon size={18} />
        </button>
        <h2>{name}</h2>
        {!!client?.pending && <Badge label={`${client.pending} čeká na přepis`} variant="warning" />}
      </div>

      <div className="tr-body">
      <div className="tr-scroll">
        <ClientSummary key={slug} slug={slug} name={name} />

        <h3 className="tr-h">Schůzky</h3>
        {meetings.isLoading && <Spinner />}
        {meetings.error && <p className="bad small">{(meetings.error as Error).message}</p>}
        {meetings.data && !meetings.data.length && <p className="muted small">Zatím žádné schůzky.</p>}
        <div className="tr-meetings">
          {meetings.data?.map((m) => {
            const ready = !m.status || m.status === 'ready';
            return (
              <button
                key={m.meetingId}
                type="button"
                className="tr-meeting"
                disabled={!ready}
                onClick={() => openTranscript({ start: m.start, t: 0, meetingId: m.meetingId })}
                title={ready ? 'Otevřít přepis' : (m.statusNote ?? 'Přepis zatím není')}
              >
                <span className="tr-meeting-d">{fmtStart(m.start)}</span>
                <span className="tr-meeting-t">{m.title}</span>
                <span className="tr-meeting-r">
                  {m.durationSec ? `${Math.round(m.durationSec / 60)} min` : ''}
                  {m.status && m.status !== 'ready' && <Badge label={STATUS[m.status].label} variant={STATUS[m.status].variant} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <ClientChat slug={slug} name={name} me={me} />
      </div>
    </div>
  );
}

/** Shrnutí klienta od Samovy pipeline (aktualizuje se po každé schůzce): nahoře stav spolupráce, podrobnosti na kliknutí. */
function ClientSummary({ slug, name }: { slug: string; name: string }) {
  const q = useQuery({
    queryKey: ['transcripts', 'summary', slug],
    queryFn: () => api<ClientSummaryDTO>('GET', `/transcripts/clients/${encodeURIComponent(slug)}/summary`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const verified = useMemo(() => new Set(q.data?.verified ?? []), [q.data?.verified]);
  // Seznam schůzek je pod kartou, ve shrnutí ho neopakovat.
  const parts = useMemo(() => [...(q.data?.sections ?? []), ...(q.data?.card ?? [])].filter((x) => !/^schůzky$/i.test(x.title)), [q.data]);
  const [open, setOpen] = useState<string | null>(null);
  const d = q.data;
  const shown = parts.find((x) => x.title === open);
  const notFound = q.error instanceof ApiError && q.error.status === 404;
  return (
    <section className="tr-sum" aria-label={`Shrnutí klienta ${name}`}>
      <div className="tr-sum-h">
        <SparkleIcon size={15} weight="fill" className="tr-sum-ico" />
        <b>Co víme o klientovi</b>
        {d?.updatedAfterMeeting && <small className="muted">aktualizováno po schůzce {fmtStart(d.updatedAfterMeeting.replace(' ', 'T'))}</small>}
      </div>
      <div className="tr-sum-b">
        {q.isLoading && <Spinner />}
        {notFound && <p className="muted small">Shrnutí tohoto klienta zatím není. Vznikne po zpracování jeho schůzek.</p>}
        {q.error && !notFound && <p className="bad small">{(q.error as Error).message}</p>}
        {d && <Markdown text={d.status} verified={verified} />}
        {!!parts.length && (
          <div className="tr-sum-tabs" role="tablist" aria-label="Podrobnosti o klientovi">
            {parts.map((x) => (
              <button
                key={x.title}
                type="button"
                role="tab"
                aria-selected={open === x.title}
                className={`tr-sum-tab${open === x.title ? ' on' : ''}`}
                onClick={() => setOpen(open === x.title ? null : x.title)}
              >
                {x.title}
              </button>
            ))}
          </div>
        )}
        {shown && (
          <div className="tr-sum-part" role="tabpanel" aria-label={shown.title}>
            <Markdown text={shown.md} verified={verified} />
          </div>
        )}
      </div>
    </section>
  );
}

/** Chat ve složce: dotazy ke klientovi bez odchodu ze stránky; Sam ví, o kterého klienta jde. */
function ClientChat({ slug, name, me }: { slug: string; name: string; me: MeDTO }) {
  const { toast } = useToast();
  const existing = useQuery({
    queryKey: ['transcripts', 'chat', slug],
    queryFn: async () => (await api<{ conversationId: string | null }>('GET', `/transcripts/clients/${encodeURIComponent(slug)}/chat`)).conversationId,
  });
  const [convId, setConvId] = useState<string | null>(null);
  // „Nový chat ke klientovi“: další dotaz založí novou konverzaci místo navázání na poslední.
  const [fresh, setFresh] = useState(false);
  // Okno s konverzací: po odeslání otevřené, jde sbalit (zůstane jen pole na dotaz).
  const [open, setOpen] = useState(false);
  const id = convId ?? (fresh ? null : (existing.data ?? null));
  const conv = useConversation(id ?? '', !!id);
  const busy = useAnyLiveTurn(id ?? '');
  const end = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLDivElement>(null);

  // Výška pole na dotaz → karta chatu ho má uvnitř dole (stejný spodek), i když pole naroste na víc řádků.
  useEffect(() => {
    const el = dock.current;
    const cmp = el?.querySelector<HTMLElement>('.cmp');
    if (!el || !cmp) return;
    const ro = new ResizeObserver(() => el.style.setProperty('--cmp-h', `${cmp.offsetHeight}px`));
    ro.observe(cmp);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const d = conv.data;
    if (!d?.activeTurnId) return;
    const m = d.messages.find((x) => x.turnId === d.activeTurnId && x.role === 'assistant');
    if (m && m.status === 'streaming') followTurn({ turnId: d.activeTurnId, conversationId: d.id, messageId: m.id });
  }, [conv.data?.activeTurnId, conv.data?.messages, conv.data?.id]);

  useEffect(() => {
    // Jen uvnitř okna chatu (scrollIntoView by posunul i stránku složky).
    const box = end.current?.closest('.msgs');
    if (open && box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [conv.data?.messages.length, open]);

  const onSend = async (text: string, attachments: AttachmentDTO[]) => {
    try {
      setOpen(true);
      const r = await sendMessage(id ?? 'new', text, attachments, { transcriptsClient: { slug, name } });
      if (!id) setConvId(r.conversation.id);
      return true;
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
      return false;
    }
  };

  const notFound = conv.error instanceof ApiError && conv.error.status === 404;
  const hasThread = !!id && !notFound && !!conv.data?.messages.length;
  // Nový chat ke klientovi: další dotaz založí novou konverzaci (stará zůstane v menu).
  const startNew = () => {
    setConvId(null);
    setFresh(true);
    setOpen(false);
  };
  return (
    // Chat je schovaný za polem na dotaz a rozbalí se z něj nahoru přes obsah složky; tlačítka jedou nad ním.
    // Lišta s tlačítky drží v rozložení stálou výšku, takže se seznam schůzek při otevření nemění.
    <div className="tr-dock" ref={dock}>
      {hasThread && (
        <div className="tr-up">
          <div className="tr-up-in">
            <div className="tr-dock-row">
              <button type="button" className="tr-thread-pill" onClick={() => setOpen(!open)} aria-expanded={open}>
                {open ? <CaretDownIcon size={14} /> : <ChatCircleIcon size={14} />}
                {open ? 'Sbalit chat' : 'Pokračovat v chatu o klientovi'}
              </button>
              <button type="button" className="tr-thread-pill" onClick={startNew}>
                <PlusIcon size={14} /> Nový chat
              </button>
            </div>
            <div className={`tr-panel${open ? ' open' : ''}`} aria-hidden={!open}>
              <div className="tr-panel-in">
                <div className="msgs" role="region" aria-label={`Chat o klientovi ${name}`}>
                  <div className="msgs-in">
                    {conv.data?.messages.map((m) => (m.role === 'user' ? <UserMessage key={m.id} m={m} /> : <AssistantMessage key={m.id} m={m} />))}
                    <div ref={end} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Composer placeholder={`Zeptejte se na klienta ${name}…`} onSend={onSend} busy={busy} disabled={me.ai !== 'ok'} />
    </div>
  );
}

